import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import {
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView";
import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  RecordGridViewService,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";
import fieldHelpers from "../../../ui-formly/fieldHelpers";

import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../shared/lookups";

import { vsObjectName as machineConfigurationTypeName } from "./machineConfigurationType";
import { vsObjectName as machineDenominationName } from "./lookup/denomination";
import { StateService } from "@uirouter/angularjs";

export const vsObjectName = "machineProgressiveGame";

machineProgressiveGameConfig.$inject = [
  FormViewServiceProviderName,
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];
export function machineProgressiveGameConfig(
  formViewServiceProvider: FormViewServiceProvider,
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  registerGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider
  );
  registerForm(formViewServiceProvider);
}

function registerGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    RecordGridViewServiceName,
    ServerUtilityName,
    GridSettingsServiceName,
    (
      recordGridViewService: RecordGridViewService,
      serverUtility: ServerUtility,
      gridSettingsService: GridSettingsService
    ) => {
      const machineConfigurationTypeGridConfiguration =
        recordGridViewService.getGridConfiguration(
          machineConfigurationTypeName
        )!;
      return {
        columnDefinitionSet: vsObjectName,
        defaultSort: RecordGridViewService.combineSort(
          machineConfigurationTypeGridConfiguration.defaultSort,
          "MachineProgressiveGameOrder"
        ),
        gridSettings: gridSettingsService.getGridSettings(Symbol(vsObjectName)),
        namePlural: "Progressive Games",
        nameSingular: "Progressive Game",
        gridDataSource: new RecordDataSource(
          serverUtility,
          "/api/machines/MachineProgressiveGame"
        ),
      };
    },
  ]);

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    RecordGridViewServiceName,
    ColumnDefinitionServiceName,
    (
      recordGridViewService: RecordGridViewService,
      columnDefinitionService: ColumnDefinitionService
    ) => {
      const machineConfigurationTypeGridConfiguration =
        recordGridViewService.getGridConfiguration(
          machineConfigurationTypeName
        )!;
      return [
        columnDefinitionService.getStringColDef({
          field: "MachineProgressiveGameName",
          header: "Game",
        }),
        ...(machineConfigurationTypeGridConfiguration.columns ?? []),
      ];
    },
  ]);
}

function registerForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    FieldConfigurationHelperServiceName,
    FormViewServiceName,
    MachineLookupManagementServiceName,
    "$state",
    (
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      formViewService: FormViewService,
      machineLookupManagementService: LookupManagementService,
      $state: StateService
    ) => {
      return {
        fields: [
          fieldConfigurationHelperService.repeaterField({
            allowAdd: true,
            displayNameSingular: "Game",
            displayNamePlural: "Games",
            repeaterFields: [
              ...fieldHelpers.columnLayout(
                [
                  fieldHelpers.simpleStringField({
                    key: "Name",
                    label: "Game Name",
                    required: true,
                  }),
                ],
                4
              ),
              fieldConfigurationHelperService.repeaterField({
                key: "Levels",
                allowAdd: true,
                displayNameSingular: "Level",
                displayNamePlural: "Levels",
                repeaterFields: [
                  ...fieldHelpers.columnLayout(
                    [
                      fieldHelpers.simpleStringField({
                        key: "Name",
                        label: "Level Name",
                        required: true,
                      }),
                      {
                        key: "Type",
                        type: "select",
                        templateOptions: {
                          label: "Type",
                          options: [
                            {
                              label: "Standalone",
                              value: "Standalone",
                            },
                            {
                              label: "Linked",
                              value: "Linked",
                            },
                            {
                              label: "WAP",
                              value: "Wap",
                            },
                          ],
                          required: true,
                          labelProp: "label",
                          valueProp: "value",
                        },
                      },
                      fieldConfigurationHelperService.lookupField(
                        machineLookupManagementService,
                        {
                          key: "MachineDenominationId",
                          label: "Denomination",
                          objectName: machineDenominationName,
                        }
                      ),
                      fieldHelpers.simpleCurrencyField({
                        key: "Ceiling",
                        label: "Ceiling",
                        min: 0,
                      }),
                      fieldHelpers.simpleCurrencyField({
                        key: "BaseAmount",
                        label: "Base Amount",
                        min: 0,
                      }),
                      fieldHelpers.simplePercentageField({
                        key: "IncrementPercentage",
                        label: "Inc%",
                        min: 0,
                      }),
                      fieldHelpers.simpleCurrencyField({
                        key: "MustHitBy",
                        label: "Must Hit By",
                        min: 0,
                      }),
                      {
                        key: "copyDelete",
                        type: "recordRepeaterCopyDelete",
                        templateOptions: {
                          label: " ",
                          allowMove: true,
                        },
                      },
                    ],
                    4
                  ),
                ],
                showHeader: true,
                allowAddBottom: false,
              }),
              {
                key: "copyDelete",
                type: "recordRepeaterCopyDelete",
                templateOptions: {
                  label: " ",
                  allowMove: true,
                },
              },
            ],
            key: "Children",
            showHeader: true,
            allowAddBottom: false,
          }),
        ],
        name: vsObjectName,
        title: () => "Progressive Games",
        submitForm: (args) =>
          formViewService
            .submitForm({
              formObject: args.model,
              formViewController: args.formViewController,
              method: "PUT",
              url: `/api/machines/MachineConfigurationType/${
                (<any>args.model)?.Id
              }/progressiveGames`,
            })
            .then(() => $state.go("^", undefined, { reload: true })),
      };
    },
  ]);
}
